/**
 * HOOK / SETUP / PAYOFF / CTA / END scenes for clip-v2.
 * Each scene renders inside an absolutely positioned box (see ClipV2.jsx) and
 * receives its own local frame.
 */
import React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";
import { formatEmphasis, formatPrice } from "../scenarioExplainerTimeline";
import {
  BrandMark,
  Chip,
  Kicker,
  MOTION,
  Slab,
  clamp,
  countValue,
  ease,
  formatCount,
  lerp,
  popIn,
  sp,
  toneColor,
} from "./ClipV2Primitives";
import { VIDEO_FONT_FAMILY } from "../../RegisterVideoFonts";
import {
  HOOK_MAX_LINES,
  HOOK_TYPE,
  URL_PILL,
  createCanvasMeasure,
  estimateTextWidth,
  fitUrl,
  hookFontSize,
  layoutHook,
} from "./clipV2TextFit";
import {
  assumptionLabel,
  dataAsOfLine,
  dataLabelTag,
  formatAssumptionValue,
  resolveHeadlineNumber,
  selectedHook,
  smartQuotes,
} from "./clipV2Timeline";

export const FIXED_DISCLAIMER_LINE = "Educational tool. Not investment advice.";

export { hookFontSize };

export function subjectLine(clip) {
  if (clip.format === "compare-two") return `${clip.ticker} · 2 trades`;
  return `${clip.ticker} · ${clip.strategy.label}`;
}

/** Brand + ticker row shared by the hook and the end card (the loop rhyme). */
export function TopRow({ clip, layout, style = {} }) {
  const { s, L } = layout;
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginRight: -(layout.reserveRight || 0), ...style }}>
      <BrandMark brand={clip.brand} size={s(L.logoSize)} fontSize={s(L.brandFont)} />
      <Chip colors={clip.brand.colors} fontSize={s(L.chipFont)}>
        {subjectLine(clip)}
      </Chip>
    </div>
  );
}

let sharedMeasure;
/** Real-font text measure in the browser (canvas); the conservative estimate elsewhere. */
export function textMeasure() {
  if (sharedMeasure === undefined) sharedMeasure = createCanvasMeasure(VIDEO_FONT_FAMILY) || estimateTextWidth;
  return sharedMeasure;
}

/**
 * Fit the hook into a box (see layoutHook): measured, stepped down in size,
 * then wrapped word by word. `baseFont` is the preferred size.
 */
export function fitHook(text, { baseFont, maxWidth, maxHeight, minFont, maxLines }) {
  return layoutHook(text, {
    baseFont: Math.round(baseFont),
    maxLines,
    minFont: Math.round(minFont ?? baseFont * 0.6),
    // Slack for subpixel / rotation differences between measure and layout.
    maxWidth: maxWidth * 0.97,
    maxHeight,
    measure: textMeasure(),
  });
}

/** Group a line's words into runs: a plain word, or one emphasis segment (one slab per run). */
function lineRuns(words) {
  const runs = [];
  words.forEach((w) => {
    const last = runs[runs.length - 1];
    if (w.emphasis && last && last.emphasis && last.group === w.group) last.words.push(w);
    else runs.push({ emphasis: w.emphasis, tone: w.tone, group: w.group, words: [w] });
  });
  return runs;
}

/**
 * Kinetic words: each word slams in; `**emphasis**` words land on a slab.
 * `fit` is a layoutHook result rendered one block per line, so nothing re-wraps
 * or overflows. An emphasized phrase split across lines keeps a slab on each line.
 */
export function KineticHook({ fit, colors, frame, fps, isStatic = false, startDelay = 0 }) {
  const { fontSize, lines } = fit;
  const total = lines.reduce((n, l) => n + l.length, 0);
  const stagger = Math.max(1, Math.min(3, Math.floor((0.6 * fps) / Math.max(1, total))));
  const wordStyle = (w) => (isStatic ? {} : popIn(frame, fps, startDelay + w.index * stagger, { from: 1.7, config: MOTION.slam }));
  const word = (w, withSuffix) => (
    <span style={{ display: "inline-block", whiteSpace: "nowrap", transformOrigin: "50% 60%", ...wordStyle(w) }}>
      {smartQuotes(w.text)}
      {withSuffix ? w.suffix : null}
    </span>
  );
  return (
    <div
      style={{
        fontSize,
        fontWeight: HOOK_TYPE.weight,
        lineHeight: HOOK_TYPE.lineHeight,
        letterSpacing: `${HOOK_TYPE.letterSpacingEm}em`,
        wordSpacing: `${HOOK_TYPE.wordSpacingEm}em`,
        color: colors.text,
      }}
    >
      {lines.map((line, li) => (
        <div key={li} style={{ whiteSpace: "nowrap" }}>
          {lineRuns(line).map((run, ri) => {
            const lead = ri > 0 ? " " : null;
            if (!run.emphasis) {
              return (
                <React.Fragment key={ri}>
                  {lead}
                  {word(run.words[0], true)}
                </React.Fragment>
              );
            }
            const first = run.words[0];
            const last = run.words[run.words.length - 1];
            const slab = isStatic ? 1 : sp(frame, fps, MOTION.settle, startDelay + first.index * stagger + 2);
            return (
              <React.Fragment key={ri}>
                {lead}
                <Slab tone={run.tone} colors={colors} fontSize={fontSize} progress={slab} style={{ transform: "rotate(-1.5deg)", whiteSpace: "nowrap" }}>
                  {run.words.map((w, wi) => (
                    <React.Fragment key={w.index}>
                      {wi > 0 ? " " : null}
                      {word(w, false)}
                    </React.Fragment>
                  ))}
                </Slab>
                {last.suffix ? <span style={{ display: "inline-block", ...wordStyle(last) }}>{last.suffix}</span> : null}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Words that slam in one after another, balanced across lines. */
export function SlamWords({ text, frame, fps, stagger = 2, delay = 0, style = {} }) {
  const words = smartQuotes(text).split(/\s+/).filter(Boolean);
  return (
    <div style={{ textWrap: "balance", ...style }}>
      {words.map((w, i) => (
        <React.Fragment key={`${i}-${w}`}>
          {i > 0 ? " " : null}
          <span style={{ display: "inline-block", ...popIn(frame, fps, delay + i * stagger, { from: 1.6 }) }}>{w}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── HOOK ────────────────────────────────────────────────────

export function HookScene({ clip, layout, frame, fps, durationInFrames }) {
  const { s, L } = layout;
  const text = selectedHook(clip);
  const maxPush = 1.045;
  const push = interpolate(frame, [0, durationInFrames], [1, maxPush], clamp);
  // Narrower column (e.g. next to a presenter PiP) → smaller preferred type.
  const narrow = Math.sqrt(Math.min(1, layout.contentWidth / (layout.width - layout.sidePad * 2)));
  const boxHeight = (layout.box || layout.band).height;
  const fit = fitHook(text, {
    baseFont: s(hookFontSize(text, L.hookFont)) * narrow,
    minFont: s(L.hookFont * 0.6) * narrow,
    maxWidth: layout.contentWidth / maxPush,
    maxHeight: (boxHeight - s(L.headerHeight)) * 0.9,
    maxLines: HOOK_MAX_LINES[layout.key],
  });
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column" }}>
      <TopRow clip={clip} layout={layout} style={{ height: s(L.headerHeight), ...popIn(frame, fps, 0, { from: 0.9 }) }} />
      <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
        <div style={{ transform: `scale(${push})`, transformOrigin: "left center" }}>
          <KineticHook fit={fit} colors={clip.brand.colors} frame={frame} fps={fps} />
        </div>
      </div>
    </AbsoluteFill>
  );
}

// ── SETUP ───────────────────────────────────────────────────

function SingleSetup({ clip, layout, frame, fps }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const spot = countValue(frame, { from: clip.spot * 0.96, to: clip.spot, start: 4, duration: 20 });
  const isChange = clip.format === "change-one";
  const a = isChange ? clip.assumption : null;
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: s(18) }}>
      <Kicker text="The trade" colors={colors} fontSize={s(L.kickerFont)} frame={frame} fps={fps} />
      <div style={{ display: "flex", alignItems: "baseline", gap: s(28), ...popIn(frame, fps, 2, { from: 1.25 }) }}>
        <div style={{ fontSize: s(L.tickerFont), fontWeight: 900, lineHeight: 0.9, letterSpacing: "-0.05em", color: colors.text }}>
          {clip.ticker}
        </div>
        <div style={{ fontSize: s(L.spotFont), fontWeight: 800, color: colors.muted, fontVariantNumeric: "tabular-nums" }}>
          {formatPrice(spot)}
        </div>
      </div>
      <div style={{ ...popIn(frame, fps, 8, { from: 0.8, rise: s(20) }), fontSize: s(L.strategyFont), fontWeight: 900, letterSpacing: "-0.02em" }}>
        <Slab colors={colors} fontSize={s(L.strategyFont)} progress={sp(frame, fps, MOTION.settle, 9)}>
          {clip.strategy.label.toUpperCase()}
        </Slab>
      </div>
      <div style={{ ...popIn(frame, fps, 13, { from: 0.95, rise: s(16) }), fontSize: s(L.legsFont), fontWeight: 700, lineHeight: 1.3, color: colors.muted }}>
        {clip.strategy.legs}
      </div>
      {isChange ? (
        <div
          style={{
            ...popIn(frame, fps, 22, { from: 0.85, rise: s(24), config: MOTION.bounce }),
            marginTop: s(18),
            alignSelf: "flex-start",
            padding: `${s(22)}px ${s(30)}px`,
            borderRadius: s(26),
            background: colors.surface,
            border: `${s(4)}px solid ${colors.primary}`,
          }}
        >
          <div style={{ fontSize: s(L.readoutLabelFont), fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: colors.muted }}>
            {`What if… ${assumptionLabel(a)}`}
          </div>
          <div style={{ fontSize: s(L.strategyFont), fontWeight: 900, color: colors.text, marginTop: s(6) }}>
            {`${formatAssumptionValue(a.kind, a.from)} → ${formatAssumptionValue(a.kind, a.to)}`}
          </div>
        </div>
      ) : null}
    </AbsoluteFill>
  );
}

function CompareSetup({ clip, layout, frame, fps }) {
  const { s, L, key } = layout;
  const { colors } = clip.brand;
  const vertical = key === "vertical";
  const card = (strategy, index) => (
    <div
      key={strategy.label}
      style={{
        ...popIn(frame, fps, 10 + index * 7, { from: 0.8, rise: s(30) }),
        flex: 1,
        padding: `${s(28)}px ${s(30)}px`,
        borderRadius: s(28),
        background: colors.surface,
        border: `${s(4)}px solid ${index === 0 ? colors.accent : colors.primary}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: s(18) }}>
        <div
          style={{
            width: s(L.cardTitleFont * 1.25),
            height: s(L.cardTitleFont * 1.25),
            borderRadius: "50%",
            background: index === 0 ? colors.accent : colors.primary,
            color: index === 0 ? colors.background : "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: s(L.cardTitleFont * 0.7),
            fontWeight: 900,
            flexShrink: 0,
          }}
        >
          {index === 0 ? "A" : "B"}
        </div>
        <div style={{ fontSize: s(L.cardTitleFont), fontWeight: 900, color: colors.text, letterSpacing: "-0.02em" }}>{strategy.label}</div>
      </div>
      <div style={{ fontSize: s(L.cardBodyFont), fontWeight: 700, color: colors.muted, marginTop: s(14), lineHeight: 1.3 }}>{strategy.legs}</div>
    </div>
  );
  const vs = (
    <div
      style={{
        ...popIn(frame, fps, 20, { from: 2, config: MOTION.bounce }),
        alignSelf: "center",
        fontSize: s(L.kickerFont * 1.1),
        fontWeight: 900,
        color: colors.background,
        background: colors.text,
        borderRadius: s(40),
        padding: `${s(8)}px ${s(24)}px`,
        margin: vertical ? `${-s(26)}px 0` : `0 ${-s(26)}px`,
        zIndex: 2,
      }}
    >
      VS
    </div>
  );
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: s(22) }}>
      <Kicker text="Same view" colors={colors} fontSize={s(L.kickerFont)} frame={frame} fps={fps} />
      <div style={{ ...popIn(frame, fps, 3, { from: 1.3 }), transformOrigin: "left center", fontSize: s(L.lessonFont), fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.05, color: colors.text }}>
        {clip.view}
      </div>
      <div style={{ display: "flex", flexDirection: vertical ? "column" : "row", alignItems: "stretch", marginTop: s(12) }}>
        {card(clip.strategies[0], 0)}
        {vs}
        {card(clip.strategies[1], 1)}
      </div>
    </AbsoluteFill>
  );
}

export function SetupScene(props) {
  return props.clip.format === "compare-two" ? <CompareSetup {...props} /> : <SingleSetup {...props} />;
}

// ── PAYOFF ──────────────────────────────────────────────────

export function bigNumberFont(text, base, maxWidth) {
  return Math.round(Math.min(base, maxWidth / Math.max(1, text.length * 0.58)));
}

export function PayoffScene({ clip, layout, frame, fps, hitFrame = 10 }) {
  const { s, L, contentWidth } = layout;
  const { colors } = clip.brand;
  const headline = resolveHeadlineNumber(clip);
  const value = countValue(frame, { from: 0, to: headline.value, start: hitFrame - 8, duration: 16 });
  const text = formatCount(value, headline.format, headline.value);
  const finalText = formatEmphasis(headline);
  const fs = bigNumberFont(finalText, s(L.bigNumberFont), contentWidth);
  const punch = sp(frame, fps, MOTION.punch, hitFrame);
  const color = headline.format === "price" ? colors.accent : toneColor(headline.value, colors);
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: s(30) }}>
      <Kicker text="The lesson" colors={colors} fontSize={s(L.kickerFont)} frame={frame} fps={fps} />
      <SlamWords
        text={clip.lesson.text}
        frame={frame}
        fps={fps}
        delay={1}
        style={{ fontSize: s(L.lessonFont), fontWeight: 900, lineHeight: 1.06, letterSpacing: "-0.035em", color: colors.text }}
      />
      <div
        style={{
          marginTop: s(20),
          opacity: interpolate(frame, [hitFrame - 8, hitFrame - 5], [0, 1], clamp),
          transform: `scale(${lerp(1.35, 1, punch)})`,
          transformOrigin: "left center",
        }}
      >
        <div style={{ fontSize: fs, fontWeight: 900, lineHeight: 0.92, letterSpacing: "-0.05em", color, fontVariantNumeric: "tabular-nums" }}>{text}</div>
        <div style={{ fontSize: s(L.bigLabelFont), fontWeight: 800, color: colors.muted, marginTop: s(12) }}>{headline.label}</div>
      </div>
    </AbsoluteFill>
  );
}

// ── CTA ─────────────────────────────────────────────────────

export function CtaScene({ clip, layout, frame, fps }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const pill = sp(frame, fps, MOTION.bounce, 8);
  const pulse = 1 + 0.06 * Math.sin(Math.PI * ease(frame, Math.round(fps * 1.1), Math.round(fps * 0.4), Easing.inOut(Easing.quad)));
  // The pill may grow ~10% (bounce overshoot, then the pulse): fit the URL to the column / 1.1.
  const baseUrlFont = s(L.urlFont);
  const url = fitUrl(clip.cta.url, {
    baseFont: baseUrlFont,
    minFont: s(L.urlFont * 0.55),
    maxWidth: (layout.contentWidth / 1.1) * 0.97,
    measure: textMeasure(),
  });
  const f = url.fontSize;
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", gap: s(44) }}>
      <div style={{ ...popIn(frame, fps, 0, { from: 1.3 }), fontSize: s(L.ctaFont), fontWeight: 900, lineHeight: 1.04, letterSpacing: "-0.04em", color: colors.text }}>
        {clip.cta.text}
      </div>
      <div
        style={{
          opacity: interpolate(frame, [8, 11], [0, 1], clamp),
          transform: `scale(${lerp(0.5, 1, pill) * pulse})`,
          // A smaller URL font keeps the pill's height: the vertical padding absorbs the difference.
          padding: `${Math.round(baseUrlFont * 0.42 + (baseUrlFont - f) * 0.5)}px ${Math.round(f * URL_PILL.padXEm)}px`,
          borderRadius: Math.round(baseUrlFont * 1.2),
          background: colors.primary,
          color: "#FFFFFF",
          fontSize: f,
          fontWeight: URL_PILL.weight,
          letterSpacing: `${URL_PILL.letterSpacingEm}em`,
          display: "flex",
          alignItems: "center",
          gap: Math.round(f * URL_PILL.gapEm),
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        <span>{url.text}</span>
        <span style={{ fontSize: Math.round(f * 0.9) }}>→</span>
      </div>
      <div style={{ ...popIn(frame, fps, 16, { from: 0.9, rise: s(14) }) }}>
        <BrandMark brand={clip.brand} size={s(L.logoSize)} fontSize={s(L.brandFont)} />
      </div>
    </AbsoluteFill>
  );
}

// ── END CARD (rhymes with the hook: same top row, same big type + slab) ──

export function modeledDataLine(dataAsOf, dataLabel = "modeled") {
  return dataAsOfLine(dataAsOf, dataLabel);
}

/** Small "Modeled" / "Market data" tag that sits on the data-as-of line. */
export function DataLabelTag({ dataLabel, colors, fontSize }) {
  const observed = dataLabel === "observed";
  return (
    <Chip
      colors={colors}
      fontSize={fontSize}
      style={{
        background: observed ? colors.primary : colors.surface,
        color: observed ? "#FFFFFF" : colors.accent,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        fontWeight: 900,
        verticalAlign: "middle",
        marginRight: fontSize * 0.6,
      }}
    >
      {dataLabelTag(dataLabel)}
    </Chip>
  );
}

export function EndScene({ clip, layout, frame, fps, durationInFrames }) {
  const { s, L } = layout;
  const { colors } = clip.brand;
  const out = interpolate(frame, [durationInFrames - 5, durationInFrames], [1, 0], clamp);
  const enter = (i) => popIn(frame, fps, i * 2, { from: 0.94, rise: s(14), config: MOTION.settle });
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column", opacity: out }}>
      <TopRow clip={clip} layout={layout} style={{ height: s(L.headerHeight) }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: s(26) }}>
        <div
          style={{
            ...enter(0),
            fontSize: s(L.endTitleFont),
            fontWeight: 900,
            lineHeight: 1.06,
            letterSpacing: "-0.035em",
            color: colors.text,
          }}
        >
          <Slab colors={colors} fontSize={s(L.endTitleFont)} progress={sp(frame, fps, MOTION.settle, 2)} style={{ transform: "rotate(-1.5deg)" }}>
            MODELED
          </Slab>{" "}
          results.
        </div>
        <div style={{ ...enter(1), fontSize: s(L.endTitleFont * 0.42), fontWeight: 900, color: colors.text, lineHeight: 1.2 }}>
          {FIXED_DISCLAIMER_LINE}
        </div>
        <div style={{ ...enter(2), fontSize: s(L.endBodyFont), fontWeight: 600, lineHeight: 1.4, color: colors.text, opacity: 0.92 }}>
          {clip.disclaimer}
        </div>
        <div style={{ ...enter(3), fontSize: s(L.endMetaFont), fontWeight: 700, lineHeight: 1.4, color: colors.muted }}>
          <DataLabelTag dataLabel={clip.dataLabel} colors={colors} fontSize={s(L.endMetaFont * 0.8)} />
          {modeledDataLine(clip.dataAsOf, clip.dataLabel)}
        </div>
      </div>
    </AbsoluteFill>
  );
}

// ── THUMBNAIL (hook frame + big number) ─────────────────────

export function ThumbnailScene({ clip, layout }) {
  const { s, L, contentWidth } = layout;
  const { colors } = clip.brand;
  const text = selectedHook(clip);
  const headline = resolveHeadlineNumber(clip);
  const numText = formatEmphasis(headline);
  const color = headline.format === "price" ? colors.accent : toneColor(headline.value, colors);
  const numFont = bigNumberFont(numText, s(L.bigNumberFont * 1.3), contentWidth);
  const gap = s(layout.key === "vertical" ? 60 : 30);
  // Hook gets what the top row, the big number block and the gap leave over.
  const numberBlock = numFont * 0.92 + s(12) + s(L.bigLabelFont) * 1.3;
  const fit = fitHook(text, {
    baseFont: s(hookFontSize(text, L.hookFont) * 0.92),
    minFont: s(L.hookFont * 0.6 * 0.92),
    maxWidth: contentWidth,
    maxHeight: (layout.band.height - s(L.headerHeight) - gap - numberBlock) * 0.92,
    maxLines: HOOK_MAX_LINES[layout.key],
  });
  return (
    <AbsoluteFill style={{ display: "flex", flexDirection: "column" }}>
      <TopRow clip={clip} layout={layout} style={{ height: s(L.headerHeight) }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap }}>
        <KineticHook fit={fit} colors={colors} isStatic />
        <div>
          <div
            style={{
              fontSize: numFont,
              fontWeight: 900,
              lineHeight: 0.92,
              letterSpacing: "-0.05em",
              color,
            }}
          >
            {numText}
          </div>
          <div style={{ fontSize: s(L.bigLabelFont), fontWeight: 800, color: colors.muted, marginTop: s(12) }}>{headline.label}</div>
        </div>
      </div>
    </AbsoluteFill>
  );
}
